import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
    OnDestroy,
    DOCUMENT, ChangeDetectionStrategy
} from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { filter, switchMap, take, takeWhile } from 'rxjs/operators';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

import {Product} from '../../../../models/product.model';
import {OrderItemDelivery} from '../../../../models/order-item-delivery.model';
import {Order} from '../../../../models/order.model';
import {OrderItem} from '../../../../models/order-item.model';
import {OrderService} from '../../../../services/order.service';
import {AuthService} from '../../../../services/auth.service';
import { ECOMMERCE_TYPE } from '../../../../constants/order-types';
import {ConfigService} from '../../../../services/config.service';
import {AddressService} from '../../../../services/address.service';
import {Address} from '../../../../models/address.model';
import * as ORDER_STATES from '../../../../constants/order-states';
import {ITEM_COMPLETE_STATE, QUOTE_STATE} from '../../../../constants/order-states';
import {BACKORDER_ALL_ITEMS, BACKORDER_PARTIAL_ITEMS} from '../../../../constants/texts';
import {ModalService} from '../../../../services/modal.service';
import {floorLastCent, isEmptyArray, updateItemInArray} from '../../../../shared/utils';
import {OrderItemSku} from '../../../../models/order-item-sku.model';
import {TranslateService} from '../../../../services/translate.service';
import {SKU} from '../../../../models/sku.model';
import {TrackingNumber, UNKNOWN_SLUG} from '../../../../interfaces/tracking-number';
import * as TRACKING_STATES from '../../../../constants/tracking-states';
import {computeDiscountPrice, computeShipmentLabel} from '../../../../shared/helpers';
import {PaymentService} from '../../../../services/payments.service';
import {MobileCheckServiceService} from '../../../../services/mobile-check-service.service';
import {MultiTenantService} from '../../../../services/multi-tenant.service';
import {ShippingMethod} from '../../../../models/shipping-method';
import {DiscountCode} from '../../../../models/discount-code.model';
import {DiscountCodeService} from '../../../../services/discount-code.service';
import { ToastrService } from 'ngx-toastr';
import { EUDRService } from '../../../../services/eudr.service';
import { OrderItemService } from '../../../../services/order-item.service';
import {AllocationQty} from '../../../../interfaces/allocation-group';
import {ProductService} from '../../../../services/product.service';

@Component({
    selector: 'app-product-order-item-delivery',
    templateUrl: './product-order-item-delivery-component.html',
    styleUrls: ['./product-order-item-delivery-component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ProductOrderItemDeliveryComponent implements OnInit, OnDestroy {

    @ViewChild('divContainer') divContainer: ElementRef;

    @Input() product: Product;

    @Input() delivery: OrderItemDelivery;

    @Input() order: Order;

    @Input() orderItem: OrderItem;

    @Input() canRemoveAddress = true;

    @Input() readonly = false;

    // Opt-in used only by the read-only cart list. That template does not render delivery-level
    // EUDR state, so creating shared-stream subscriptions and rescanning the whole item per row is waste.
    @Input() cartReadonlyPerformanceMode = false;

    private hasPrecomputedEudrState = false;
    @Input() set precomputedEudrRestricted(value: boolean | null | undefined) {
        this.hasPrecomputedEudrState = value !== null && value !== undefined;
        if (this.hasPrecomputedEudrState) {
            this.showEudrWarning = value;
        }
    }

    get showReadonlyEudrWarning(): boolean {
        return this.hasPrecomputedEudrState && this.showEudrWarning;
    }

    @Input() showBudget = false;

    @Input() showEditControls = false;

    @Input() showTotalInline = false;

    @Input() isLastShipment = false;

    @Input() canAddAnotherAddress = false;

    @Input() isFirstShipment = false;

    @Input() viewType: string;

    @Input() totals: number;

    @Input() paidQuantity: number;

    @Input() showTrackingInfo = false;

    @Input() updatingTrackingInfo = false;

    // @Output() addressRemoved = new EventEmitter<void>(); // output event emits when delivery is removed

    // @Output() quantityChanged = new EventEmitter<void>(); // output event emits when delivery quantity is changed

    // @Output() addressChanged = new EventEmitter<void>(); // output event emits when address is changed

    @Output() onRemove = new EventEmitter<OrderItem>();

    @Output() customizationChanged = new EventEmitter<OrderItemDelivery>(); // emits when customization data changed

    // emits when deliver_to / contact details changed, carrying the item as the server saved it
    @Output() deliverToChanged = new EventEmitter<OrderItem>();

    @Output() changed = new EventEmitter<OrderItem>();

    @Output() refreshStatus = new EventEmitter<void>();

    @Output() onEditContactDetails = new EventEmitter<OrderItemDelivery>();

    @Output() orderItemSubmitRequired = new EventEmitter<boolean>();

    @Output() restrictionChange = new EventEmitter<boolean>();

    shippingMethods: ShippingMethod[] = [];
    isSingleShippingMethod = false;

    addresses: Address[] = [];

    private backOrderWarning = '';


    trackingNumbers: TrackingNumber[] = [];
    status: string;
    trackingData: string;
    shippedDate: Date;
    shippedQuantity: number;

    show = 'text';

    updatingDeliveryId: number = null;

    _refreshingTrackingStatus = false;

    // _shipToAddressContainerCss = '';
    // _shippingMethodContainerCSS = '';
    // _internalOrderContainerCss = '';
    _showInputLabel = true;

    private hasShippingMethodError  = false;

    _discountCodes: DiscountCode[] = [];
    _selectedDiscountCodeId: number | null = null;
    _selectedDiscountCode: DiscountCode | null = null;

    // this check is required for discount codes
    // on cart page they have to be eaditable otherwise not
    private _isCardPage = false;

    private alive = true;
    public eudrFooterMessageText: string | null = null;
    public showEudrWarning = false;

    isOutOfStock$: Observable<boolean>;
    @Input() set refreshingTrackingStatus(val: boolean) {
        if (val === false && this._refreshingTrackingStatus) {
            // redraw tracking data when refreshing is finished;
            this.initTrackingData();
        }
        this._refreshingTrackingStatus = val;
    };


    constructor( private orderService: OrderService,
                 private configService: ConfigService,
                 private authService: AuthService,
                 private addressService: AddressService,
                 private modalService: ModalService,
                 private translateService: TranslateService,
                 private ngbModalService: NgbModal,
                 private paymentService: PaymentService,
                 private mobileCheck: MobileCheckServiceService,
                 private multiTenantService: MultiTenantService,
                 @Inject(DOCUMENT) private _document: Document,
                 private discountCodeService: DiscountCodeService,
                 private toastr: ToastrService,
                 private orderItemService: OrderItemService,
                 private eudrService: EUDRService,
                 private productService: ProductService,
                 ) {}


    ngOnInit(): void {

        this.calcAvailableShipmentMethods();

        this.addresses = this.addressService.addresses;

        this.detectBackorder();

        this.initTrackingData();

        // this._showInputLabel = this.showInputLabel;
        // always show labels
        this.isFirstShipment = true;

        // Initialise  available discount codes
        const address = this.delivery.getAddress()?.id ? this.delivery.getAddress() : null;
        const availableDiscountCodes = this.getAvailableDiscountCodes(address, this.orderItem);
        if (availableDiscountCodes && availableDiscountCodes.length) {
            // Use existing discount code from delivery if available, otherwise leave unselected
            this._selectedDiscountCodeId = this.delivery.discountCodeId;
            this._selectedDiscountCode = this.delivery.discount_code;

            this._discountCodes = this._selectedDiscountCode
                ? updateItemInArray<DiscountCode>(availableDiscountCodes, this._selectedDiscountCode, true)
                : availableDiscountCodes;
            this.updateDiscount(availableDiscountCodes);
        } else {
            this.clearDiscount();
        }

        this._isCardPage = this.checkIsCurrentPageCart;

        if (!(this.readonly && this.cartReadonlyPerformanceMode)) {
            // Initial EUDR check for views that display or edit delivery-level restrictions.
            if (!this.hasPrecomputedEudrState && this.product && address) {
                this.eudrService.checkEudrForAddress(this.product, address);
                this.orderItemService.updateEudrStateForWholeItem(this.orderItem);
                this.showEudrWarning = this.eudrService.checkEudrRestrictions(this.product, address);
                this.restrictionChange.emit(this.orderItemService.isEudrRestricted(this.orderItem));
            }

            if (!this.hasPrecomputedEudrState) {
                // Show toast exactly once when restriction appears
                this.eudrService.getErrorMessage()
                    .pipe(
                        takeWhile(() => this.alive),
                        filter(msg => !!msg),
                        take(1)
                    )
                    .subscribe((errorMsg: string) => {
                        this.toastr.error(errorMsg);
                    });

                // Subscribe to EUDR footer message
                this.eudrService.getFooterMessage()
                    .pipe(takeWhile(() => this.alive))
                    .subscribe(message => {
                        this.eudrFooterMessageText = message;
                    });
            }

            this.isOutOfStock$ = this.checkInventoryAllocationOutOfStock$();
        }
    }

    ngOnDestroy(): void {
        this.alive = false;
    }

    private initTrackingData() {
        this.trackingNumbers = this.delivery.normalizedTrackingNumbers;

        if (this.trackingNumbers.length === 0) {
            // tracking data not available
            this.trackingData = 'N/A';
            this.status = (this.order.state === ORDER_STATES.COMPLETE_STATE) ? TRACKING_STATES.DELIVERED : TRACKING_STATES.PENDING;
        } else if (this.trackingNumbers.length === 1) {
            const trackingNumber = this.trackingNumbers[0];

            this.shippedDate = this.trackingNumbers[0].shipping_date;
            this.shippedQuantity = this.trackingNumbers[0].quantity;
            // single tracking number case
            this.status = trackingNumber.carrier_name !== UNKNOWN_SLUG ? trackingNumber.status :
                (this.order.state === ORDER_STATES.COMPLETE_STATE) ? TRACKING_STATES.DELIVERED : TRACKING_STATES.PENDING;
            this.show = 'link';
            if (this.validateTrackingNumber(this.trackingNumbers[0].number)) {
                this.show = 'vendor';
                this.trackingData = this.trackingNumbers[0].number;
            }
        } else {
            // multiple tracking case

            // find global tracking status
            this.status = (this.trackingNumbers.filter(
                t => t.status === TRACKING_STATES.DELIVERED && t.carrier_name !== UNKNOWN_SLUG).length === this.trackingNumbers.length) ?
                TRACKING_STATES.DELIVERED : TRACKING_STATES.IN_TRANSIT;
            this.show = 'modal';

            this.trackingNumbers.forEach(tracking => {
                tracking.mode =  this.validateTrackingNumber(tracking.number) ? 'vendor' : 'link';
            });
        }

    }


    validateTrackingNumber(value: string): boolean {
        return (value === 'MISC' || value === 'Call Client Service');
    }

    showDetails(content) {
        this.ngbModalService.open(content).result.then(() => undefined, () => undefined);
    }


    isDelivered(val): boolean {
        return val === TRACKING_STATES.DELIVERED;
    }


    /**
     * checks whether item can be purchased
     * in case of BUYING_WINDOW project  ow_purchasable == false - purchase is not available
     */
    get canPurchase(): boolean {
        if (!this.order) {
            return false;
        }

        if (this.paymentService.hasFailedTransactions) {
            return false;
        }

        return this.orderItem.deliveries.length !== 0 &&
            (  this.order.type === ECOMMERCE_TYPE ?
                (
                    (this.orderItem.init_quantity + this.delivery.quantity) > 0
                    || this.product.inStock
                    || this.product.isEnabledAllocatedInventory
                )
                : this.product.ow_purchasable === true);
    }


    updateAddressData(address: Address): void {
        if (!this.canOrder || !address || !this.product) {
            this.toastr.error('Cannot update address: missing required data');
            return;
        }

        this.delivery.setAddress(address, this.authService.user, {
            canOrder: true,
            isEmployee: this.authService.isEmployee
        }, this.product);

        // Update EUDR state for this address based on the result of setting the address
        this.showEudrWarning = this.eudrService.checkEudrRestrictions(this.product, address);

        // Update global EUDR state for the whole order item
        this.orderItemService.updateEudrStateForWholeItem(this.orderItem);

        this.delivery.data ??= {};
        this.delivery.unchanged = false;
        this.delivery.method = null;
        this.delivery.allocation_group_id = undefined;
        this.calcAvailableShipmentMethods();

        const discountCodes = this.getAvailableDiscountCodes(address, this.orderItem);
        this.updateDiscount(discountCodes);

        this.orderItem.updateDelivery(this.delivery);
        this.changed.emit(this.orderItem);
    }

    private updateDiscount(discountCodes: DiscountCode[]): void {
        this._discountCodes = [...discountCodes];

        // Clear selection if currently selected discount is no longer available
        if (
            this._selectedDiscountCodeId &&
            !discountCodes.some(d => d.id === this._selectedDiscountCodeId)
        ) {
            this._selectedDiscountCodeId = null;
            this._selectedDiscountCode = null;
        }

        const currentCouponId = this.delivery.discount_code?.id || this.delivery.app_coupon_id;
        if (currentCouponId && !this.discountCodeService.getDiscountCodeById(currentCouponId)) {
            this._selectedDiscountCodeId = null;
            this._selectedDiscountCode = null;
            this.delivery.app_coupon_id = null;
            this.delivery.discount_code = null;
        }
    }

    /**
     * Clear discount variables
     */
    private clearDiscount(): void {
        this._selectedDiscountCodeId = null;
        this._selectedDiscountCode = null;
        this.delivery.app_coupon_id = null;
        this.delivery.discount_code = null;
        this._discountCodes = [];
    }


    updateShippingMethod(event) {
        if (event) {
            this.delivery.method = event.method;
            this.orderItem.updateDelivery(this.delivery);
            this.changed.emit(this.orderItem);
        }
    }

    updateDiscountCode(event: DiscountCode | null) {
        if (event) {
            this._selectedDiscountCodeId = event.id;
            this._selectedDiscountCode = event;
            this.delivery.app_coupon_id = event.id;
            this.delivery.discount_code = event;
            this.orderItem.updateDelivery(this.delivery);
            this.changed.emit(this.orderItem);
        } else {
            this._selectedDiscountCodeId = null;
            this._selectedDiscountCode = null;
            this.delivery.app_coupon_id = null;
            this.delivery.discount_code = null;
            this.orderItem.updateDelivery(this.delivery);
            this.changed.emit(this.orderItem);
        }
        this.orderItemSubmitRequired.emit(true);
    }

    removeAddress() {
        this.orderItem.removeDelivery(this.delivery);
        // Re-check EUDR after removal
        this.orderItemService.updateEudrStateForWholeItem(this.orderItem);
        this.changed.emit(this.orderItem);
    }

    get completed(): boolean {
        return this.orderItem.state === ITEM_COMPLETE_STATE;
    }

    onInternalOrderChanged(newValue: string) {
        const value  =  newValue ? newValue.trim() : '';
        if (this.order.isWBSDefined) {
            this.delivery.wbs = value;
        } else {
            this.delivery.internal_order = value;
        }
        this.orderItem.updateDelivery(this.delivery);
        // this.changed.emit(this.orderItem);
    }

    onInternalOrderReset() {
        this.delivery.wbs = '';
        this.delivery.internal_order = '';
        this.orderItem.updateDelivery(this.delivery);
        // this.changed.emit(this.orderItem);
    }


    get internalOrderLabel(): string {
        return this.configService.getInternalOrderLabel(this.order?.isWBSDefined, this.order?.isNotReal);
    }


    onQuantityChanged(event) {
        const newValue: number = +event.target.value || 0;
        if (this.orderItem.isAvailable) {
            // ensure quantity is not null
            this.delivery.quantity = this.orderItem.validateDeliveryQuantity(this.delivery, newValue);
            // this.checkQuantity();

            if (!this.product.hasVariations &&
                !this.product.isBuyingWindow &&
                this.product.isEnabledAllocatedInventory) {

                const firstSkuId = this.product.firstSku?.id;
                if (firstSkuId) {
                    this.delivery.saveAllocationGroupQuantity(firstSkuId, this.delivery.quantity);
                }
            }
            this.detectBackorder();
        }
    }

    onQuantityBlur() {
        // emit change event only after blur event  to keep focus on input
        this.deliveryChanged();
        return true;
    }

    private deliveryChanged( ) {
        this.orderItem.changed = true;
        this.changed.emit(this.orderItem);
    }

    onHandleRemove() {
        this.onRemove.emit(this.orderItem);
    }

    private detectBackorder() {
        if (this.order.type === ECOMMERCE_TYPE) {
            const pendingQuantity = this.orderItem.getPendingQuantity();
            if (pendingQuantity) {

                const inStockQuantity = this.orderItem.getInStockQuantity();

                if (inStockQuantity === 0 && this.delivery.quantity > 0) {
                    // all items go to backorder
                    this.backOrderWarning = BACKORDER_ALL_ITEMS;
                } else if ( inStockQuantity < this.delivery.quantity) {
                    // partial items go to backorder
                    const backOrderAmount = this.delivery.quantity - inStockQuantity;
                    this.backOrderWarning = BACKORDER_PARTIAL_ITEMS.replace( '${0}', '' + backOrderAmount);
                } else {
                    this.backOrderWarning = '';
                }
            }

        }
    }


    get canCustomize(): boolean {
        return this.order.isDecisionPoint !== true
            && this.authService.canEditOrder(this.order)
            && !this.paymentService.hasFailedTransactions;
    }

    get canEditDeliveryTo(): boolean {
        if (this.orderItem?.restricted) {
            return false;
        }

        if (this.isItemCut) {
            return false;
        }
        return this.order.state === QUOTE_STATE && this.authService.canEditOrder(this.order);
    }


    get canEditSAP(): boolean {
        return this.order.state === QUOTE_STATE;
    }

    // editInternalOrder(delivery: OrderItemDelivery) {
    //     if (!this.canEditInternalOrder) {
    //         return;
    //     }
    //
    //     this.modalService.showInternalOrderModal(this.order, this.orderItem, delivery).then( retAttention => {
    //         if (retAttention) {
    //             this.deliverToChanged.emit();
    //         }
    //     });
    // }

    editSAP(delivery: OrderItemDelivery) {
        if (!this.canEditSAP) {
            return;
        }

        const costCenter = delivery.cost_center ||  this.order.costCenter;


        from(this.modalService.showSelectSAPModal(costCenter)).pipe(
            switchMap( retCostCenter => {
                if (!retCostCenter) {
                    return of(null);
                }

                this.delivery.cost_center = retCostCenter;
                this.updatingDeliveryId = delivery.id;
                return this.orderService.updateOrderItem(this.order, this.orderItem);
            })
        ).subscribe( retOrder => {

            if (retOrder) {
                const retOrderItem = retOrder.items.find( i => i.id === this.orderItem.id);
                if (retOrderItem) {
                    const retDelivery = retOrderItem.deliveries.find( d => d.id === delivery.id);
                    if (retDelivery) {
                        this.delivery = new OrderItemDelivery(retDelivery);
                        this.deliverToChanged.emit(retOrderItem);
                    }
                }
            }

            this.updatingDeliveryId = null;
        });
    }


    public getShipToLabel(delivery: OrderItemDelivery): string {
        return computeShipmentLabel(delivery);
    }

    public getSapInfo(delivery: OrderItemDelivery): string {
        return delivery.cost_center ? delivery.cost_center.getLongAccount() : this.order.costCenter.getLongAccount();
    }

    private  calcAvailableShipmentMethods() {

        const findAvailableShippingMethodsByCountryCode =
          (shippingMethods: ShippingMethod[], countryCode: string): ShippingMethod[] | undefined => {
            if (!countryCode) {
                return [];
            }
            return shippingMethods.filter( s => s.country === countryCode);
        }

        if (!this.delivery || !this.product) {
            return;
        }

        // reset options at the beginning
        this.shippingMethods = [];
        this.isSingleShippingMethod = false;

        const availableShippingMethods =
          findAvailableShippingMethodsByCountryCode(this.product.shipping_methods, this.delivery.country_code);

        if (isEmptyArray(availableShippingMethods)) {
            this.hasShippingMethodError = true;
            this.delivery.method = null;
            return;
        }

        if (availableShippingMethods.length === 1) {
            this.isSingleShippingMethod = true;
        }



        if (!this.delivery.method) {
            // only  if method is not set yet
            this.delivery.method = availableShippingMethods[0].method;
        }


        this.shippingMethods =  availableShippingMethods;
    }


    getVariationLabel(sku: OrderItemSku): string {
        const productSku = this.getProductSku(sku);
        if (!productSku) {
            return;
        }

        const labels = productSku.attributes.sort( (a, b) => {
            return (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0);
        }).map( attribute => {
            const label = this.translateService.translate(attribute.label);
            return `${label}: ${attribute.options[0].label}`;
        });

        return labels.join(', ');

    }

    getVariationUrn(sku: OrderItemSku): string {
        if (sku.urn) {
            return sku.urn;
        }

        return this.getProductSku(sku)?.urn ?? '';
    }

    get productCodeLabel(): string {
        return this.multiTenantService.productCodeLabel || 'Product Code';
    }

    /**
     * Returns the variation's product code/client sku info for display.
     * - If supplier_id exists (Supplier foreign key) → returns Product Code label
     * - If supplier_id is null but client_sku_number exists → returns Client SKU label
     */
    getVariationCodeInfo(sku: OrderItemSku): { label: string, value: string } | null {
        const productSku = this.getProductSku(sku);
        if (!productSku) {
            return null;
        }

        if (productSku.supplier_id) {
            return { label: this.productCodeLabel, value: productSku.supplier_id };
        }

        if (productSku.client_sku_number) {
            return { label: 'Client SKU', value: productSku.client_sku_number };
        }

        return null;
    }
    getVariationPrice(sku: OrderItemSku): number {
        let price = 0;
        let discount = 0;
        const productSKU = this.getProductSku(sku);
        if (!productSKU) {
            return price;
        }

        price  = Number(sku.product_price || 0);
        discount = Number(sku.discount_price || 0);
        return floorLastCent(computeDiscountPrice(price, discount));
    }

    getVariationTotal(sku: OrderItemSku): number {
        const productSKU = this.getProductSku(sku);

        if (!productSKU) {
            return 0;
        }

        const price = this.getVariationPrice(sku);
        const skuQuantity = sku?.quantity || 0;
        return floorLastCent(price * skuQuantity);
    }


    getProductSku(sku: OrderItemSku): SKU {
        if (!sku) {
            return;
        }

        return this.orderItem.product.skus.find( s => s.id === sku.sku_id);
    }

    showDPFaqDialog() {
        this.modalService.showDecisionPointFaq(this.order.attr_decision_point_name);
    }

    get discount(): number {
        if (this.orderItem) {
            return this.orderItem.getProductDiscount();
        }

        return this.product?.discount || 0;
    }

    get canOrder(): boolean {
        return this.authService.canOrder;
    }

    get mobile(): boolean {
        return this.mobileCheck.mobile;
    }

    get mobileDevice(): boolean {
        return this.mobileCheck.mobileDevice;
    }

    get qtyContainerCSS(): string {
        const styles: string[] = [];

        if (this.viewType === 'list-item') {
            styles.push('list-item');
        }

        // if (this.deliveryColumns === 2) {
        //     styles.push('half-width');
        // }


        if (this.mobile) {
            styles.push(' px-0 pr-2');
        }

        return styles.join(' ');
    }


    get canShowInternalOrder(): boolean {
        return this.multiTenantService.canShowWBS || this.multiTenantService.canShowInternalOrder || this.multiTenantService.canShowBillingCodeDropdown;
    }

    findMethodLabel(product: Product, delivery: OrderItemDelivery): string {
        if (!product || !delivery) {
            return '';
        }

        const method = delivery.method;
        const addressCountry = delivery.country_code;
        let shippingMethod = null;
        if (method || addressCountry) {
            shippingMethod = product.shipping_methods.find( m => m.method === method && m.country === addressCountry);
        }

        return shippingMethod ? shippingMethod.method_label : '';
    }

    editContactDetails(delivery: OrderItemDelivery) {
        if (!delivery) {
           return;
        }

        this.modalService.showEditContactDetailsModal({order: this.order,
            orderItem: this.orderItem, orderItemDelivery: this.delivery})
          .subscribe( result => {
            if (result) {
                // pass the saved item up: the cart no longer refetches the items list, so
                // without it the view would keep rendering the pre-save contact details
                this.deliverToChanged.emit(result.orderItem);
            }
        })

    }

    get isItemCut(): boolean {
        return this.orderItem?.item_cut ? this.orderItem.item_cut : false;
    }


    get hasDiscountCodes(): boolean {
        return this.discountCodeService.hasDiscountCodes;
    }

    private getAvailableDiscountCodes(address: Address = null, orderItem: OrderItem | null = null): DiscountCode[] {
        // Early return if no orderItem, product, or discount codes
        if (!orderItem?.product || !this.discountCodeService.discountCodes?.length) {
            return [];
        }

        const discountCodes = this.discountCodeService.discountCodes;
        const orderType = this.order.getOrderType();

        // Base filter for eligible discounts
        const isEligibleDiscount = (discount: DiscountCode) =>
            !isEmptyArray(discount?.ordering_type) &&
            (discount.ordering_type.includes(orderType) || discount.ordering_type.includes(UNKNOWN_SLUG)) &&
            this.discountCodeService.isProductEligibleForDiscount(orderItem.product, discount);

        // Filter discounts based on address presence
        if (!address) {
            return discountCodes.filter(discount =>
                isEligibleDiscount(discount) && !discount.assigned_addresses.length
            );
        }

        // Get address-specific discounts
        const addressDiscounts = this.discountCodeService.getDiscountCodesForAddress(address, orderItem);


        // Filter eligible discounts that match address-specific discounts
        return discountCodes.filter(discount =>
            isEligibleDiscount(discount) &&
            addressDiscounts.some(addressDiscount => addressDiscount.id === discount.id)
        );
    }

    get isDiscountCodesSearchable(): boolean {
        return false;
    }

    resetDiscount() {
        this.updateDiscountCode(null);
    }

    get canEditDiscountCodes(): boolean {
        return this.readonly && this._isCardPage;
    }


    get checkIsCurrentPageCart(): boolean {

        const paths  = window.location.pathname.split('/');
        return paths.includes('cart') && !paths.includes('checkout');
    }

    get hasDiscountCode(): boolean {
        return !!this._selectedDiscountCode?.code;
    }

    get hasDiscountValue(): boolean {
        return !!this._selectedDiscountCode?.discount;
    }

    // filter  skus with quantity > 0
    orderedSkus(): OrderItemSku[] {
        if (!this.orderItem || !this.orderItem.skus) {
            return [];
        }

        return this.orderItem.skus.filter( sku => sku.quantity > 0);
    }

    public get mainContactVisibility(): boolean {
        return this.configService.addressBookFeatures.showMainContactName;
    }

    public get showEditContactButton(): boolean {
        return this.showTrackingInfo === false && this.mainContactVisibility && this.delivery.getAddress().isWarehouse === false;
    }

    public get showHiddenEditContactButton(): boolean {
        return this.showTrackingInfo === false && this.mainContactVisibility && this.delivery.getAddress().isWarehouse === true;
    }

    public handleCustomizationChanged(delivery: OrderItemDelivery) {
      if (!delivery) {
        return;
      }

      this.delivery =  delivery;
      this.customizationChanged.emit(this.delivery);
    }

    onEudrRestrictionChanged(): void {
        this.orderItemService.updateEudrStateForWholeItem(this.orderItem);
    }

  protected handleUpdateAllocationGroup(allocationGroupId: number) {
    this.delivery.allocation_group_id = allocationGroupId;
    this.orderItem.updateDelivery(this.delivery);
    this.changed.emit(this.orderItem);
  }

  // handle OD product without variations
  protected handleUpdateInventoryAllocation(allocationQty: AllocationQty) {
      if (!this.delivery) {
        return;
      }

      // only for OD product without variations
      if (this.product.hasVariations) {
          return;
      }

      // only for allocated inventory enabled products
      if (!this.product.isEnabledAllocatedInventory) {
          return;
      }

      const firstSkuId = this.product.firstSku?.id;
      if (!firstSkuId) {
          // no sku found
          return;
      }

      this.delivery.saveAllocationGroup(firstSkuId, {allocation_group_id: allocationQty.allocation_group_id,
          allocation_group_label: allocationQty.allocation_group_label
      });

  }

  checkInventoryAllocationOutOfStock$(): Observable<boolean> {
    if (!this.product
      || this.product.isBuyingWindow
      || !this.product.isEnabledAllocatedInventory) {
        return of(false);
    }

    if (this.product.hasVariations) {
        return this.productService.isProductInventoryAllocationOutOfStock(this.product.id);
    }

    return this.productService.isInventoryAllocationOutOfStock(this.product.id);
  }


  //
  get ondemandProductHasInventoryAllocations(): boolean {
    return this.product.isBuyingWindow === false &&  !isEmptyArray(this.delivery?.allocation_groups);
  }


  getAllocationGroupLabelBySkuId(skuId: number): string {

    const allocation = this.delivery?.allocation_groups?.find( ia => ia.sku_id === skuId);
    return allocation?.allocation_group_label || '';
  }

  get isBuyingWindow(): boolean {
    return this.product?.isBuyingWindow === true;
  }

  get hasVariations(): boolean {
    return this.product?.hasVariations === true;
  }

  get isEnabledAllocatedInventory(): boolean {
    return this.product?.isEnabledAllocatedInventory === true;
  }

  get hideOnDemandAllocationControl(): boolean {
    return this.isBuyingWindow || this.hasVariations || !this.isEnabledAllocatedInventory;
  }

  get hideBuyingWindowAllocationControl(): boolean {
    return !this.isBuyingWindow || !this.isWarehouseAddressIsSelected;
  }

  // get someBuyingWindowDeliveriesHaveAllocationGroup(): boolean {
  //   if (!this.isBuyingWindow) {
  //     return false;
  //   }
  //
  //   if (isEmptyArray(this.orderItem?.deliveries)) {
  //     return false;
  //   }
  //
  //   if (!this.readonly) {
  //     return this.orderItem?.product.isEnabledAllocatedInventory
  //       && this.orderItem?.deliveries.some(d => d.getAddress().isWarehouse === true);
  //   }  else {
  //     return this.orderItem?.product.isEnabledAllocatedInventory
  //       && this.orderItem?.deliveries.some(d => !!d.allocation_group_id);
  //   }
  // }

  get isWarehouseAddressIsSelected(): boolean {
      return this.delivery?.getAddress()?.isWarehouse === true;
  }

  // product has been added to a cart already
  get isItemInCart(): boolean {
    return !!this.orderItem?.id;
  }

  get canEditOrderItems(): boolean {
    return this.order ? this.orderService.canEditOrderItems(this.order) : false;
  }

  //
  isFieldDisabled(outOfStock: boolean): boolean {
    if (!this.canEditOrderItems) {
      return true;
    }

    if (this.isItemInCart) {
      return false;
    }

    return !this.canPurchase || this.isItemCut || outOfStock;
  }

}
